import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { LoginService } from 'src/app/modules/login/login.service';
import { ServiceLogsService } from '../_services/service-logs.service';
import {
    ServiceLogFile, ServiceLogInstance, ServiceLogLine, ServiceLogService
} from '../_types/service-log';

/// Visor de los registros de los servicios en segundo plano. Sigue el fichero en vivo sondeando la
/// API con el offset de la vuelta anterior, de forma que cada petición sólo trae lo nuevo.
///
/// Los ficheros están en el recurso compartido que la VM y el App Service montan; ver
/// GUAI.Service/Logging/ServiceLogPaths.cs y ServiceLogsCustomController.
@Component({
    selector: 'app-service-logs-viewer',
    templateUrl: './service-logs-viewer.component.html',
    styleUrls: ['./service-logs-viewer.component.scss'],
    standalone: false
})
export class ServiceLogsViewerComponent implements OnInit, OnDestroy {

    public static readonly PERMISSION_ACCESS = 'SERVICE_LOGS.ACCESS';
    public static readonly PERMISSION_DOWNLOAD = 'SERVICE_LOGS.DOWNLOAD';

    /// Cada cuánto se pide el tramo nuevo. Tres segundos es "en vivo" para un log que se escribe por
    /// ciclos de 60 s, y no castiga a la API si alguien deja la pestaña abierta toda la mañana.
    private static readonly POLL_MS = 3000;

    /// Tope de renglones en memoria. Al pasarse se tiran los más antiguos: sin esto una sesión larga
    /// sobre un servicio parlanchín acaba tumbando el navegador.
    private static readonly MAX_LINES = 5000;

    /// El fichero que recoge lo que se registra fuera de los servicios (arranque, MQTT, FastAGI).
    /// Se llama así en disco para que ordene el primero; en pantalla tiene nombre propio.
    private static readonly GENERAL_LOG = '_General';

    public canDownload = false;

    public instances: ServiceLogInstance[] = [];
    public selectedInstance: string | null = null;
    public selectedService: string | null = null;

    public lines: ServiceLogLine[] = [];
    public files: ServiceLogFile[] = [];

    public tab: 'live' | 'files' = 'live';
    public paused = false;
    public loadingCatalog = false;
    public catalogError: string | null = null;
    public tailError: string | null = null;

    public levelFilter: string | null = null;
    public levelOptions = [
        { value: 'ERROR', name: 'Sólo errores' },
        { value: 'WARN', name: 'Avisos y errores' },
        { value: 'INFO', name: 'Información y superiores' }
    ];
    public textFilter = '';

    /// Desplazamiento automático al final. Se apaga solo en cuanto el usuario sube, porque si no es
    /// imposible leer nada en un servicio que escribe seguido.
    public followTail = true;

    @ViewChild('console') private consoleElement?: ElementRef<HTMLElement>;

    private offset: number | null = null;
    private pollSubscription: Subscription | null = null;
    private tailSubscription: Subscription | null = null;
    private inFlight = false;
    private pendingScroll = false;
    private readonly onVisibilityChange = () => this.syncPolling();

    constructor(private serviceLogsService: ServiceLogsService, private loginService: LoginService) { }

    public ngOnInit(): void {
        this.canDownload = this.loginService.hasPermission(ServiceLogsViewerComponent.PERMISSION_DOWNLOAD);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.loadCatalog();
    }

    public ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopPolling();
        this.tailSubscription?.unsubscribe();
    }

    public loadCatalog(): void {
        this.loadingCatalog = true;
        this.catalogError = null;

        this.serviceLogsService.getCatalog().subscribe({
            next: (instances) => {
                this.instances = instances;
                this.loadingCatalog = false;

                //si el servicio que se estaba mirando ya no existe se limpia la selección, para no
                //quedarse sondeando un fichero que ha desaparecido
                if (this.selectedService && !this.findService(this.selectedInstance, this.selectedService)) {
                    this.selectedInstance = null;
                    this.selectedService = null;
                    this.stopPolling();
                }
            },
            error: (error: HttpErrorResponse) => {
                this.loadingCatalog = false;
                this.catalogError = error.status === 403
                    ? 'No tienes permiso para ver los logs de los servicios.'
                    : 'No se ha podido leer el directorio de logs.';
            }
        });
    }

    public select(instance: string, service: ServiceLogService): void {
        if (this.selectedInstance === instance && this.selectedService === service.name) return;

        this.selectedInstance = instance;
        this.selectedService = service.name;

        //arranque limpio: el contenido de otro servicio no tiene nada que ver con este
        this.lines = [];
        this.files = [];
        this.offset = null;
        this.tailError = null;
        this.followTail = true;

        if (this.tab === 'files') this.loadFiles();
        this.readTail();
        this.syncPolling();
    }

    public setTab(tab: 'live' | 'files'): void {
        this.tab = tab;
        if (tab === 'files') this.loadFiles();
        this.syncPolling();
    }

    public togglePause(): void {
        this.paused = !this.paused;
        this.syncPolling();
        //al reanudar se pide de inmediato, sin esperar al siguiente tic
        if (!this.paused) this.readTail();
    }

    public goToEnd(): void {
        this.followTail = true;
        this.scrollToEnd();
    }

    /// El seguimiento se apaga en cuanto el usuario se separa del final y se reengancha si vuelve.
    /// El margen de 40px evita que un rebote del propio navegador lo desactive.
    public onConsoleScroll(): void {
        const element = this.consoleElement?.nativeElement;
        if (!element) return;

        this.followTail = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
    }

    public get visibleLines(): ServiceLogLine[] {
        const text = this.textFilter.trim().toLowerCase();
        const minimum = this.levelFilter;

        if (!text && !minimum) return this.lines;

        return this.lines.filter((line) => {
            //las marcas de corte se ven siempre: esconderlas daría una continuidad que no existe
            if (line.level === 'GAP') return true;
            if (minimum && !this.matchesLevel(line.level, minimum)) return false;
            if (text && !line.text.toLowerCase().includes(text)) return false;
            return true;
        });
    }

    public get selectedServiceDetail(): ServiceLogService | null {
        return this.findService(this.selectedInstance, this.selectedService);
    }

    /// Nombre para pantalla: el fichero general se llama _General en disco para que ordene primero.
    public displayName(serviceName: string): string {
        return serviceName === ServiceLogsViewerComponent.GENERAL_LOG ? 'General (arranque y otros)' : serviceName;
    }

    /// Semáforo de actividad del fichero. Sin fecha de última escritura reciente el servicio no está
    /// girando, aunque el proceso siga vivo.
    public freshness(service: ServiceLogService): 'fresh' | 'stale' | 'old' {
        const minutes = (Date.now() - new Date(service.lastWriteAtUtc).getTime()) / 60000;
        if (minutes < 5) return 'fresh';
        if (minutes < 60) return 'stale';
        return 'old';
    }

    public formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    public loadFiles(): void {
        if (!this.selectedInstance || !this.selectedService) return;

        this.serviceLogsService.getFiles(this.selectedInstance, this.selectedService).subscribe({
            next: (files) => this.files = files,
            error: () => this.files = []
        });
    }

    public download(file: ServiceLogFile): void {
        if (!this.selectedInstance || !this.selectedService) return;

        this.serviceLogsService.downloadFile(this.selectedInstance, this.selectedService, file.fileName).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = `${this.selectedInstance}-${file.fileName}`;
                anchor.click();
                URL.revokeObjectURL(url);
            }
        });
    }

    /// El sondeo sólo corre cuando tiene sentido: con un servicio elegido, en la pestaña del directo,
    /// sin pausa manual y con la pestaña del navegador a la vista. Lo último importa: una ventana
    /// olvidada en segundo plano estaría pidiendo cada tres segundos para siempre.
    private syncPolling(): void {
        const shouldPoll = !!this.selectedService
            && this.tab === 'live'
            && !this.paused
            && document.visibilityState === 'visible';

        if (shouldPoll && !this.pollSubscription) {
            this.pollSubscription = interval(ServiceLogsViewerComponent.POLL_MS).subscribe(() => this.readTail());
        } else if (!shouldPoll) {
            this.stopPolling();
        }
    }

    private stopPolling(): void {
        this.pollSubscription?.unsubscribe();
        this.pollSubscription = null;
    }

    private readTail(): void {
        if (!this.selectedInstance || !this.selectedService) return;

        //una sola petición en vuelo: si la anterior tarda más que el periodo de sondeo, las
        //respuestas podrían llegar desordenadas y el offset avanzaría hacia atrás
        if (this.inFlight) return;
        this.inFlight = true;

        const instance = this.selectedInstance;
        const service = this.selectedService;

        this.tailSubscription = this.serviceLogsService.getTail(instance, service, this.offset).subscribe({
            next: (tail) => {
                this.inFlight = false;

                //la respuesta puede llegar después de haber cambiado de servicio
                if (instance !== this.selectedInstance || service !== this.selectedService) return;

                this.tailError = null;
                this.offset = tail.offset;

                if (tail.truncated) {
                    this.lines.push({ text: '··· se han omitido líneas (rotación del fichero o exceso de volumen) ···', level: 'GAP' });
                }

                if (tail.content) {
                    for (const text of tail.content.split('\n')) {
                        if (text.length > 0) this.lines.push({ text, level: this.levelOf(text) });
                    }
                }

                if (this.lines.length > ServiceLogsViewerComponent.MAX_LINES) {
                    this.lines = this.lines.slice(this.lines.length - ServiceLogsViewerComponent.MAX_LINES);
                }

                if (this.followTail) this.scheduleScroll();
            },
            error: (error: HttpErrorResponse) => {
                this.inFlight = false;
                if (instance !== this.selectedInstance || service !== this.selectedService) return;

                this.tailError = error.status === 404
                    ? 'El fichero ya no está disponible.'
                    : 'No se ha podido leer el log.';
                //se detiene el sondeo para no repetir el mismo error cada tres segundos
                this.stopPolling();
            }
        });
    }

    /// El renglón trae el nivel al principio, según el patrón de log4net "%-5p %d ...".
    private levelOf(text: string): ServiceLogLine['level'] {
        const head = text.substring(0, 5).trim().toUpperCase();
        if (head === 'ERROR' || head === 'FATAL') return 'ERROR';
        if (head === 'WARN') return 'WARN';
        if (head === 'INFO') return 'INFO';
        if (head === 'DEBUG') return 'DEBUG';
        return 'OTHER';
    }

    /// El filtro es "de este nivel hacia arriba": elegir avisos enseña también los errores.
    private matchesLevel(level: ServiceLogLine['level'], minimum: string): boolean {
        const order: { [key: string]: number } = { DEBUG: 0, INFO: 1, OTHER: 1, WARN: 2, ERROR: 3 };
        return (order[level] ?? 1) >= (order[minimum] ?? 0);
    }

    /// El desplazamiento se hace después de que Angular haya pintado los renglones nuevos; hacerlo
    /// en el mismo turno dejaría scrollHeight con el valor de antes.
    private scheduleScroll(): void {
        if (this.pendingScroll) return;
        this.pendingScroll = true;

        setTimeout(() => {
            this.pendingScroll = false;
            this.scrollToEnd();
        });
    }

    private scrollToEnd(): void {
        const element = this.consoleElement?.nativeElement;
        if (element) element.scrollTop = element.scrollHeight;
    }

    private findService(instance: string | null, service: string | null): ServiceLogService | null {
        if (!instance || !service) return null;

        return this.instances.find((m) => m.instance === instance)?.services
            .find((m) => m.name === service) ?? null;
    }
}
